/**
 * FocusAreaFormModal
 *
 * Shared modal for both adding and editing a focus area. In add mode, the
 * modal includes a `practice_text` field and its onSubmit payload matches
 * FocusAreaCreatePayload. In edit mode, the practice field is omitted (that's
 * handled by a separate PATCH /practice endpoint) and onSubmit receives
 * { name, description }.
 *
 * Validation mirrors the backend schema:
 * - name: required, <= 120 chars
 * - description: required, <= 2000 chars
 * - practice_text (add mode only): required, <= 280 chars
 *
 * In add mode, a "Suggest practice" button calls the LLM to pre-fill the
 * practice field from the current name+description. Rejects with Error('CAP')
 * surface the 5-active-cap error inline.
 */

import { useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { suggestPractice } from "@/services/focusAreas";
import type { FocusArea } from "@/types/focusArea";

type AddSubmit = {
  name: string;
  description: string;
  practice_text: string;
};
type EditSubmit = { name: string; description: string };

interface AddProps {
  open: boolean;
  mode: "add";
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: AddSubmit) => Promise<void>;
}

interface EditProps {
  open: boolean;
  mode: "edit";
  focusArea: FocusArea;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: EditSubmit) => Promise<void>;
}

type FocusAreaFormModalProps = AddProps | EditProps;

const NAME_MAX = 120;
const DESC_MAX = 2000;
const PRACTICE_MAX = 280;

export function FocusAreaFormModal(props: FocusAreaFormModalProps) {
  const { open, mode, onOpenChange, onSubmit } = props;
  const initial =
    mode === "edit"
      ? {
          name: props.focusArea.name,
          description: props.focusArea.description,
        }
      : { name: "", description: "" };

  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description);
  const [practiceText, setPracticeText] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const validate = (): string | null => {
    if (!name.trim()) return "Name is required";
    if (name.length > NAME_MAX) return `Name must be <= ${NAME_MAX} characters`;
    if (!description.trim()) return "Description is required";
    if (description.length > DESC_MAX)
      return `Description must be <= ${DESC_MAX} characters`;
    if (mode === "add") {
      if (!practiceText.trim()) return "Practice is required";
      if (practiceText.length > PRACTICE_MAX)
        return `Practice must be <= ${PRACTICE_MAX} characters`;
    }
    return null;
  };

  const handleSuggestPractice = async () => {
    if (!name.trim() || !description.trim()) {
      setFormError(
        "Add a name and description first, then suggest a practice."
      );
      return;
    }
    setFormError(null);
    setSuggesting(true);
    try {
      const { practice_text } = await suggestPractice({
        name: name.trim(),
        description: description.trim(),
      });
      setPracticeText(practice_text);
    } catch {
      toast.error("Couldn't generate a practice. Try again in a moment.");
    } finally {
      setSuggesting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setFormError(err);
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      if (mode === "add") {
        await onSubmit({
          name: name.trim(),
          description: description.trim(),
          practice_text: practiceText.trim(),
        });
      } else {
        await onSubmit({
          name: name.trim(),
          description: description.trim(),
        });
      }
      onOpenChange(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg === "CAP") {
        setFormError(
          "Cap reached: you already have 5 active focus areas. Retire one first, then try again."
        );
      } else {
        setFormError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === "add" ? "Add focus area" : "Edit focus area"}
          </DialogTitle>
          <DialogDescription>
            {mode === "add"
              ? "Name a behavior you want to work on. Add a concrete practice in the form of: When X happens, try Y."
              : "Update the name or description."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="fa-name">Name</Label>
            <Input
              id="fa-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Defensiveness"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="fa-description">Description</Label>
            <Textarea
              id="fa-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="When I feel cornered, I go silent."
              rows={3}
            />
          </div>
          {mode === "add" && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label htmlFor="fa-practice">Practice</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => void handleSuggestPractice()}
                  disabled={suggesting || submitting}
                  className="gap-1 h-7 text-xs"
                >
                  <Sparkles className="w-3 h-3" />
                  {suggesting ? "Suggesting..." : "Suggest practice"}
                </Button>
              </div>
              <Textarea
                id="fa-practice"
                value={practiceText}
                onChange={(e) => setPracticeText(e.target.value)}
                placeholder="When finances come up, pause for 3 breaths before responding."
                rows={3}
              />
            </div>
          )}
          {formError && (
            <div className="text-sm text-destructive" role="alert">
              {formError}
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {mode === "add" ? "Add" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
